// Solar Insights JavaScript for Shopify Liquid Template
// Integrates with Google Solar API to display solar potential data

class SolarInsights {
  constructor() {
    this.apiKey = 'AIzaSyDNyMD2fdhkHsK34OXaT12WsKSifq6GDQs';
    this.map = null;
    this.geocoder = null;
    this.currentLocation = null;
    this.sunroofLayer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    const calculateBtn = document.getElementById('calculate-solar');
    const addressInput = document.getElementById('address-input');
    const sunroofToggle = document.getElementById('sunroof-toggle');

    if (calculateBtn) {
      calculateBtn.addEventListener('click', () => this.calculateSolarPotential());
    }

    if (addressInput) {
      addressInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.calculateSolarPotential();
        }
      });
    }

    if (sunroofToggle) {
      sunroofToggle.addEventListener('change', (e) => this.toggleSunroofLayer(e.target.checked));
    }
  }

  async calculateSolarPotential() {
    const addressInput = document.getElementById('address-input');
    const address = addressInput.value.trim();

    if (!address) {
      this.showError('Please enter an address');
      return;
    }

    this.showLoading(true);
    this.hideError();

    try {
      // First, geocode the address to get coordinates
      const coordinates = await this.geocodeAddress(address);
      if (!coordinates) {
        throw new Error('Unable to find the specified address');
      }

      this.currentLocation = coordinates;

      // Get building insights from Solar API
      const buildingInsights = await this.getBuildingInsights(coordinates.lat, coordinates.lng);
      
      // Display the results
      this.displaySolarInsights(buildingInsights);
      this.initializeMap(coordinates, buildingInsights);
      this.showResults(true);

    } catch (error) {
      console.error('Error calculating solar potential:', error);
      this.showError(error.message || 'Unable to calculate solar potential for this address');
    } finally {
      this.showLoading(false);
    }
  }

  async geocodeAddress(address) {
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${this.apiKey}`;
    
    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return {
          lat: location.lat,
          lng: location.lng,
          formattedAddress: data.results[0].formatted_address
        };
      } else {
        throw new Error('Address not found');
      }
    } catch (error) {
      throw new Error('Failed to geocode address');
    }
  }

  async getBuildingInsights(lat, lng) {
    const solarApiUrl = `https://solar.googleapis.com/v1/buildingInsights:findClosest?location.latitude=${lat}&location.longitude=${lng}&key=${this.apiKey}`;

    try {
      const response = await fetch(solarApiUrl);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Solar data not available for this location');
        }
        throw new Error(`Solar API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch solar data');
    }
  }

  displaySolarInsights(insights) {
    const solarDataContainer = document.getElementById('solar-data');
    
    if (!insights || !insights.solarPotential) {
      solarDataContainer.innerHTML = '<p>No solar data available for this location.</p>';
      return;
    }

    const solarPotential = insights.solarPotential;
    const roofSegmentStats = solarPotential.roofSegmentStats || [];
    const solarPanelConfigs = solarPotential.solarPanelConfigs || [];
    const financialAnalyses = solarPotential.financialAnalyses || [];

    let html = '';

    // Basic building information
    if (insights.name) {
      html += this.createDataItem('Building', insights.name);
    }

    // Roof area information
    if (roofSegmentStats.length > 0) {
      const totalArea = roofSegmentStats.reduce((sum, segment) => sum + (segment.stats?.areaMeters2 || 0), 0);
      html += this.createDataItem('Total Roof Area', `${Math.round(totalArea)} m²`);
      
      const sunnyArea = roofSegmentStats
        .filter(segment => segment.pitchDegrees < 30) // Assuming flatter roofs get more sun
        .reduce((sum, segment) => sum + (segment.stats?.areaMeters2 || 0), 0);
      html += this.createDataItem('Suitable Roof Area', `${Math.round(sunnyArea)} m²`);
    }

    // Solar panel configuration
    if (solarPanelConfigs.length > 0) {
      const maxConfig = solarPanelConfigs.reduce((max, config) => 
        (config.yearlyEnergyDcKwh || 0) > (max.yearlyEnergyDcKwh || 0) ? config : max
      );

      html += this.createDataItem('Max Solar Panels', maxConfig.panelsCount || 'N/A');
      html += this.createDataItem('Annual Energy Production', `${Math.round(maxConfig.yearlyEnergyDcKwh || 0).toLocaleString()} kWh`);
    }

    // Financial analysis
    if (financialAnalyses.length > 0) {
      const analysis = financialAnalyses[0];
      if (analysis.monthlyBill) {
        html += this.createDataItem('Monthly Savings', `$${Math.round(analysis.monthlyBill.amount || 0)}`);
      }
      if (analysis.panelConfigIndex !== undefined && solarPanelConfigs[analysis.panelConfigIndex]) {
        const config = solarPanelConfigs[analysis.panelConfigIndex];
        html += this.createDataItem('Recommended Panels', config.panelsCount || 'N/A');
      }
    }

    // Carbon offset
    if (solarPanelConfigs.length > 0) {
      const maxConfig = solarPanelConfigs[solarPanelConfigs.length - 1];
      const carbonOffset = (maxConfig.yearlyEnergyDcKwh || 0) * 0.0004; // Rough estimate: 0.4 kg CO2 per kWh
      html += this.createDataItem('Annual CO₂ Offset', `${Math.round(carbonOffset).toLocaleString()} kg`);
    }

    solarDataContainer.innerHTML = html || '<p>Limited solar data available for this location.</p>';
  }

  createDataItem(label, value) {
    return `
      <div class="solar-data-item">
        <span class="solar-data-label">${label}:</span>
        <span class="solar-data-value">${value}</span>
      </div>
    `;
  }

  initializeMap(coordinates, buildingInsights) {
    const mapContainer = document.getElementById('google-map');
    
    this.map = new google.maps.Map(mapContainer, {
      center: { lat: coordinates.lat, lng: coordinates.lng },
      zoom: 20,
      mapTypeId: 'satellite',
      tilt: 0
    });

    // Add a marker for the building
    new google.maps.Marker({
      position: { lat: coordinates.lat, lng: coordinates.lng },
      map: this.map,
      title: 'Solar Analysis Location',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#FFD700"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 15)
      }
    });

    // Store building insights for sunroof layer
    this.buildingInsights = buildingInsights;
  }

  async toggleSunroofLayer(show) {
    if (!this.map || !this.currentLocation) {
      return;
    }

    if (show && !this.sunroofLayer) {
      try {
        // Show loading indicator for sunroof layer
        this.showSunroofLoading(true);
        
        // Get data layers for sunroof visualization
        const dataLayers = await this.getDataLayers(this.currentLocation.lat, this.currentLocation.lng);
        this.createSunroofLayer(dataLayers);
        
        this.showSunroofLoading(false);
      } catch (error) {
        console.error('Error loading sunroof layer:', error);
        this.showSunroofLoading(false);
        // Reset toggle if failed
        document.getElementById('sunroof-toggle').checked = false;
        this.showError('Unable to load sun/shade visualization for this location');
      }
    }

    if (this.sunroofLayer) {
      if (Array.isArray(this.sunroofLayer)) {
        // Multiple layers
        this.sunroofLayer.forEach(layer => {
          layer.setMap(show ? this.map : null);
        });
      } else {
        // Single layer
        this.sunroofLayer.setMap(show ? this.map : null);
      }
    }
  }

  async getDataLayers(lat, lng) {
    const dataLayersUrl = `https://solar.googleapis.com/v1/dataLayers:get?location.latitude=${lat}&location.longitude=${lng}&radiusMeters=100&view=FULL_LAYERS&key=${this.apiKey}`;

    try {
      const response = await fetch(dataLayersUrl);
      
      if (!response.ok) {
        throw new Error(`Data layers API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error('Failed to fetch sunroof data');
    }
  }

  createSunroofLayer(dataLayers) {
    if (!dataLayers || !dataLayers.imageryLayers) {
      this.createFallbackSunroofLayer();
      return;
    }

    const layers = [];
    
    // Try to find different types of solar visualization layers
    const layerTypes = ['ANNUAL_FLUX', 'MONTHLY_FLUX', 'HOURLY_SHADE', 'DSM_LAYER'];
    
    for (const layerType of layerTypes) {
      const layer = dataLayers.imageryLayers.find(l => l.layerType === layerType);
      
      if (layer && layer.imageUrl && dataLayers.imageryBounds) {
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(dataLayers.imageryBounds.south, dataLayers.imageryBounds.west),
          new google.maps.LatLng(dataLayers.imageryBounds.north, dataLayers.imageryBounds.east)
        );

        const overlay = new google.maps.GroundOverlay(
          layer.imageUrl,
          bounds,
          {
            opacity: layerType === 'ANNUAL_FLUX' ? 0.7 : 0.5,
            clickable: false
          }
        );

        layers.push(overlay);
        
        // Use the first available layer as primary
        if (!this.sunroofLayer) {
          this.sunroofLayer = overlay;
        }
        
        // For now, just use the first layer found
        break;
      }
    }

    // If no imagery layers found, create fallback
    if (layers.length === 0) {
      this.createFallbackSunroofLayer();
    } else if (layers.length > 1) {
      // Store multiple layers for future enhancement
      this.sunroofLayer = layers;
    }
  }

  createFallbackSunroofLayer() {
    // Create a simple colored overlay to indicate solar potential
    // This is a fallback when detailed sunroof data isn't available
    
    if (!this.buildingInsights || !this.buildingInsights.solarPotential) {
      // Create a basic circular overlay around the location
      const circle = new google.maps.Circle({
        center: { lat: this.currentLocation.lat, lng: this.currentLocation.lng },
        radius: 50, // 50 meter radius
        fillColor: '#FFD700',
        fillOpacity: 0.3,
        strokeColor: '#FFA500',
        strokeOpacity: 0.8,
        strokeWeight: 2
      });
      
      this.sunroofLayer = circle;
      return;
    }

    // Use building insights to create a more accurate representation
    const solarPotential = this.buildingInsights.solarPotential;
    const roofSegments = solarPotential.roofSegmentStats || [];
    
    if (roofSegments.length > 0) {
      const overlays = [];
      
      roofSegments.forEach((segment, index) => {
        if (segment.center && segment.stats) {
          // Create different colored overlays based on solar potential
          const suitability = this.calculateSolarSuitability(segment);
          const color = this.getSolarColor(suitability);
          
          const circle = new google.maps.Circle({
            center: { 
              lat: segment.center.latitude, 
              lng: segment.center.longitude 
            },
            radius: Math.sqrt(segment.stats.areaMeters2 || 25), // Radius based on area
            fillColor: color.fill,
            fillOpacity: 0.4,
            strokeColor: color.stroke,
            strokeOpacity: 0.8,
            strokeWeight: 1
          });
          
          overlays.push(circle);
        }
      });
      
      this.sunroofLayer = overlays.length > 1 ? overlays : overlays[0];
    } else {
      // Fallback to simple rectangle
      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(this.currentLocation.lat - 0.0005, this.currentLocation.lng - 0.0005),
        new google.maps.LatLng(this.currentLocation.lat + 0.0005, this.currentLocation.lng + 0.0005)
      );

      this.sunroofLayer = new google.maps.Rectangle({
        bounds: bounds,
        fillColor: '#FFD700',
        fillOpacity: 0.3,
        strokeColor: '#FFA500',
        strokeOpacity: 0.8,
        strokeWeight: 2
      });
    }
  }

  calculateSolarSuitability(segment) {
    // Calculate solar suitability based on pitch and azimuth
    const pitch = segment.pitchDegrees || 0;
    const azimuth = segment.azimuthDegrees || 180; // Default to south-facing
    
    // Ideal conditions: 15-40 degree pitch, south-facing (135-225 degrees)
    let suitability = 1.0;
    
    // Pitch factor (ideal around 30 degrees)
    if (pitch < 15 || pitch > 40) {
      suitability *= 0.7;
    }
    
    // Azimuth factor (ideal around 180 degrees - south)
    const azimuthDiff = Math.abs(azimuth - 180);
    if (azimuthDiff > 45) {
      suitability *= 0.8;
    }
    
    return Math.max(0.3, suitability); // Minimum 30% suitability
  }

  getSolarColor(suitability) {
    // Return colors based on solar suitability
    if (suitability >= 0.8) {
      return { fill: '#00FF00', stroke: '#00CC00' }; // Green - excellent
    } else if (suitability >= 0.6) {
      return { fill: '#FFFF00', stroke: '#CCCC00' }; // Yellow - good
    } else if (suitability >= 0.4) {
      return { fill: '#FFA500', stroke: '#CC8400' }; // Orange - fair
    } else {
      return { fill: '#FF6B6B', stroke: '#CC5555' }; // Red - poor
    }
  }

  showSunroofLoading(show) {
    // Add a small loading indicator for sunroof layer
    const toggle = document.getElementById('sunroof-toggle');
    const parent = toggle.parentElement;
    
    let loader = parent.querySelector('.sunroof-loader');
    
    if (show && !loader) {
      loader = document.createElement('span');
      loader.className = 'sunroof-loader';
      loader.innerHTML = ' <small>Loading...</small>';
      loader.style.color = '#666';
      parent.appendChild(loader);
    } else if (!show && loader) {
      loader.remove();
    }
  }

  showLoading(show) {
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.display = show ? 'block' : 'none';
    }
  }

  showResults(show) {
    const results = document.getElementById('results-section');
    if (results) {
      results.style.display = show ? 'block' : 'none';
    }
  }

  showError(message) {
    const errorDiv = document.getElementById('error-message');
    if (errorDiv) {
      errorDiv.querySelector('p').textContent = message;
      errorDiv.style.display = 'block';
    }
  }

  hideError() {
    const errorDiv = document.getElementById('error-message');
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }
  }
}

// Initialize the Solar Insights application
let solarInsights;

// Google Maps callback function
function initMap() {
  // Initialize the SolarInsights class when Google Maps is loaded
  solarInsights = new SolarInsights();
}

// Fallback initialization if Google Maps fails to load
document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit for Google Maps to load
  setTimeout(() => {
    if (typeof google === 'undefined') {
      console.error('Google Maps failed to load');
      document.getElementById('error-message').style.display = 'block';
      document.getElementById('error-message').querySelector('p').textContent = 
        'Google Maps failed to load. Please check your internet connection and try again.';
    } else if (!solarInsights) {
      solarInsights = new SolarInsights();
    }
  }, 3000);
});

